import React, { useEffect, useRef, useState } from 'react';
import { FixedSizeList } from 'react-window';
import { Trans, useTranslation } from 'react-i18next';
import AutoSizer from 'react-virtualized-auto-sizer';

import {
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TextField,
} from '@mui/material';

import FilterAltIcon from '@mui/icons-material/FilterAlt';
import CloseIcon from '@mui/icons-material/Close';

import StyledTable from '../styled/Table';
import StyledIconButton from '../styled/IconButton';
import StyledTableRow from '../styled/TableRow';
import StyledTableCell from '../styled/TableCell';
import StyledTableSortLabel from '../styled/TableSortLabel';
import { Restore } from '@mui/icons-material';

import { TableRow } from '@mui/material';
import { useSelector } from 'react-redux';

const VirtualTable = ({
    rows,
    columns,
    filter = false,
    rowId = 'index',
    rowHeight = 32,
    overscanCount = 48,
    gridTemplateColumns,
    item,
    action,
    onRowClick,
    selectedChecker,
    isProductFixtures
}) => {
    const [orderBy, setOrderBy] = useState(null);
    const [direction, setDirection] = useState('asc');
    const [filterColumn, setFilterColumn] = useState(null);
    const [filterValue, setFilterValue] = useState('');
    const [disableScroll, setDisableScroll] = useState(false);

    const [sortedFiltered, setSortedFiltered] = useState(rows || []);
    const [availableHeight, setAvailableHeight] = useState(285);

    const { t } = useTranslation();

    const selectedMeshId = useSelector((state) => state.project.selectedMeshId);
    const selectedProjectElement = useSelector((state) => state.scannedProject.selectedElement);

    const defaultTemplate = `repeat(${columns.length}, 1fr)`;

    const listRef = useRef(null);

    const selectedIndex = sortedFiltered.findIndex(row =>
        selectedChecker ? selectedChecker(row) : row?.selected
    );

    const infoWindowOpened = selectedMeshId || selectedProjectElement ? true : false;

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (listRef.current && selectedIndex >= 0) {
                console.log('scrolled');
                listRef.current.scrollToItem(selectedIndex, 'top');
                setDisableScroll(false);
            }
        }, 0);

        return () => clearTimeout(timeout);
    }, [sortedFiltered, selectedIndex, disableScroll]);

    const VirtualTableRow = ({ index, style }) => {
        const row = sortedFiltered[index];

        const getDate = (date) => {
            const d = new Date(date);

            return d.getDate() + '.' + (d.getMonth() + 1) + '.' + d.getFullYear() + ' ' + d.getHours() + ':' + d.getMinutes();
        };

        const renderData = (row, c) => {
            if (c.double) {
                return Number(row[c.id]).toFixed(2);
            }

            if (c.isDate) {
                return getDate(row[c.id]);
            }

            if (c.isRevert) {
                if (row['is_current']) {
                    return <Trans>Current</Trans>;
                }

                return (
                    <Restore
                        color="primary"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => action(row['id'])}
                    />
                );
            }

            return row[c.id];
        };

        return (
            <StyledTableRow
                virtual
                selected={selectedChecker ? selectedChecker(row) : row?.selected}
                style={style}
                component="div"
                key={`${row[rowId]}`}
                className={index % 2 === 1 ? 'odd' : 'even'}
                sx={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplateColumns || defaultTemplate,
                    cursor: onRowClick ? 'pointer' : 'default',
                }}
            >
                {columns.map((c) => (
                    <StyledTableCell
                        key={c.id}
                        component="div"
                        title={row[c.id]}
                        sx={c.style}
                        onClick={onRowClick ? () => {onRowClick(row, 'list'); setDisableScroll(true); console.log('asd') } : undefined}
                    >
                        {(c.id != 'description')
                            ? renderData(row, c)
                            : (
                                <div>
                                    <div>
                                        {row['description']}
                                    </div>
                                    <div>
                                        {row['description2']}
                                    </div>
                                </div>
                            )
                        }
                    </StyledTableCell>
                ))}
            </StyledTableRow>
        );
    };

    const handleSort = (id) => {
        if (id === orderBy) {
            if (direction === 'asc') {
                setDirection('desc');
            }
            if (direction === 'desc') {
                setDirection('asc');
                setOrderBy(null);
            }
        } else {
            setDirection('asc');
            setOrderBy(id);
        }
    };

    useEffect(() => {
        const filtered = filter && filterColumn && filterValue
            ? rows.filter(
                (r) => `${r[filterColumn]}`.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0
            ) : rows.map(r => r);
        const sorted = filtered;
        const dir = direction === 'asc' ? 1 : -1;
        const numbers = columns.filter(c => c.numeric).map(c => c.id);
        const numeric = orderBy && numbers.includes(orderBy);
        sorted.sort((a, b) => {
            const va = a[orderBy];
            const vb = b[orderBy];
            if (va === vb) return 0;
            if (numeric) {
                const x = Number(va) || 0;
                const y = Number(vb) || 0;
                return (x > y ? 1 : -1) * dir;
            }
            if (typeof va === 'string') {
                return va.localeCompare(vb) * dir;
            }
            return (va > vb ? 1 : -1) * dir;
        });
        setSortedFiltered(sorted);
    }, [orderBy, direction, filterColumn, filterValue, rows, columns, filter]);

    return (
        <TableContainer sx={{ width: '100%', height: '100%', overflow: 'scroll hidden' }}>
            <StyledTable component="div" size="small" sx={{ height: '100%' }}>
                <TableHead component="div" sx={{ width: 'auto', height: 'auto' }}>
                    <StyledTableRow
                        component="div"
                        sx={{
                            width: '100%',
                            display: 'grid',
                            gridTemplateColumns: gridTemplateColumns || defaultTemplate,
                        }}
                    >
                        {columns.map((c) => (
                            <StyledTableCell
                                key={c.id}
                                padding="none"
                                component="div"
                                sx={{ display: 'flex', ...c.style }}
                            //align={c.numeric ? 'right' : 'left'}
                            >
                                {filter && (
                                    <StyledIconButton
                                        size="small"
                                        color="secondary"
                                        onClick={() => {
                                            setFilterColumn(filterColumn === c.id ? null : c.id);
                                            setFilterValue('');
                                        }}
                                        sx={{ padding: 0 }}
                                    >
                                        {filterColumn === c.id ? <CloseIcon fontSize="small" /> : <FilterAltIcon fontSize="small" />}
                                    </StyledIconButton>
                                )}
                                {filter && filterColumn === c.id ? (
                                    <TextField
                                        autoFocus
                                        size="small"
                                        value={filterValue}
                                        onChange={(e) => setFilterValue(e.target.value)}
                                        placeholder={t(c.title)}
                                        variant="standard"
                                        color="secondary"
                                    />
                                ) : (
                                    <StyledTableSortLabel
                                        direction={direction}
                                        active={orderBy === c.id}
                                        onClick={() => handleSort(c.id)}
                                    >
                                        <Trans>{c.title}</Trans>
                                    </StyledTableSortLabel>
                                )}
                            </StyledTableCell>
                        ))}
                    </StyledTableRow>
                </TableHead>
                
                <TableBody
                    component="div"
                    sx={{ width: 'auto', height: availableHeight + 'px' }}
                >
                    {rows && rows.length > 0 && (
                        <AutoSizer key={infoWindowOpened}>
                            {({ height, width }) => {
                                setAvailableHeight(height);

                                return (
                                    <FixedSizeList
                                        ref={listRef}
                                        height={height}
                                        width={width}
                                        itemCount={sortedFiltered.length}
                                        itemSize={rowHeight}
                                        overscanCount={overscanCount}
                                        style={{ overflow: 'hidden scroll' }}
                                        children={VirtualTableRow}
                                    />
                                )
                            }}
                        </AutoSizer>
                    )}
                </TableBody>
            </StyledTable>
        </TableContainer>
    );
};

export default VirtualTable;
